import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import type { OffsetPageQueryDTO } from "@/shared/contracts/base";
import { campRegistrationsApi } from "./client";

export function useCampEventsQuery() {
  return useQuery({
    queryKey: ["camp-events"],
    queryFn: () => campRegistrationsApi.listEvents(),
  });
}

export function useInitializeSummerCamp2026Mutation() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: () => campRegistrationsApi.initializeSummerCamp2026(),
    onSuccess: (result) => {
      qc.invalidateQueries({ queryKey: ["camp-events"] });
      qc.invalidateQueries({ queryKey: ["camp-event", result.eventId] });
    },
  });
}

export function useCampEventQuery(id: string | undefined) {
  return useQuery({
    queryKey: ["camp-event", id],
    queryFn: () => campRegistrationsApi.getEvent(id!),
    enabled: !!id,
  });
}

export function useCampRegistrationsQuery(
  query: OffsetPageQueryDTO & {
    eventId?: string;
    status?: string;
    classTypeId?: string;
    search?: string;
  },
) {
  return useQuery({
    queryKey: ["camp-registrations", query],
    queryFn: () => campRegistrationsApi.listRegistrations(query),
  });
}

export function useCampRegistrationQuery(id: string | undefined) {
  return useQuery({
    queryKey: ["camp-registration", id],
    queryFn: () => campRegistrationsApi.getRegistration(id!),
    enabled: !!id,
  });
}

export function useCampSimilarStudentsQuery(
  id: string | undefined,
  enabled: boolean,
) {
  return useQuery({
    queryKey: ["camp-registration-similar-students", id],
    queryFn: () => campRegistrationsApi.getSimilarStudents(id!),
    enabled: !!id && enabled,
  });
}

export function useUpdateCampEventMutation() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: ({
      id,
      body,
    }: {
      id: string;
      body: Parameters<typeof campRegistrationsApi.updateEvent>[1];
    }) => campRegistrationsApi.updateEvent(id, body),
    onSuccess: (_, { id }) => {
      qc.invalidateQueries({ queryKey: ["camp-events"] });
      qc.invalidateQueries({ queryKey: ["camp-event", id] });
    },
  });
}

export function useUpdateCampEventClassesMutation() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: ({
      id,
      options,
    }: {
      id: string;
      options: Parameters<typeof campRegistrationsApi.updateEventClasses>[1];
    }) => campRegistrationsApi.updateEventClasses(id, options),
    onSuccess: (_, { id }) => {
      qc.invalidateQueries({ queryKey: ["camp-event", id] });
    },
  });
}

export function useUpdateCampRegistrationMutation() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: ({
      id,
      body,
    }: {
      id: string;
      body: Parameters<typeof campRegistrationsApi.updateRegistration>[1];
    }) => campRegistrationsApi.updateRegistration(id, body),
    onSuccess: (_, { id }) => {
      qc.invalidateQueries({ queryKey: ["camp-registrations"] });
      qc.invalidateQueries({ queryKey: ["camp-registration", id] });
    },
  });
}

export function useConvertCampRegistrationMutation() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: ({
      id,
      body,
    }: {
      id: string;
      body: Parameters<typeof campRegistrationsApi.convertRegistration>[1];
    }) => campRegistrationsApi.convertRegistration(id, body),
    onSuccess: (_, { id }) => {
      qc.invalidateQueries({ queryKey: ["camp-registrations"] });
      qc.invalidateQueries({ queryKey: ["camp-registration", id] });
    },
  });
}

export function useCampWaitlistQuery(
  eventId: string | undefined,
  classTypeId?: string,
) {
  return useQuery({
    queryKey: ["camp-waitlist", eventId, classTypeId],
    queryFn: () => campRegistrationsApi.listWaitlist(eventId!, classTypeId),
    enabled: !!eventId,
    refetchInterval: 30_000,
  });
}

export function usePromoteWaitlistMutation() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (id: string) => campRegistrationsApi.promoteWaitlist(id),
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: ["camp-waitlist"] });
      qc.invalidateQueries({ queryKey: ["camp-event"] });
    },
  });
}

export function useSkipWaitlistMutation() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (id: string) => campRegistrationsApi.skipWaitlist(id),
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: ["camp-waitlist"] });
      qc.invalidateQueries({ queryKey: ["camp-event"] });
    },
  });
}
